/**
 * Wave 3 · 23 #9 — "Chase supplier" for a late LPO.
 *  - The message is prepared by the server (POST /lpos/{id}/chase-draft): worded by the AI when AI extras
 *    (`ai_extras`, Admin › Integrations, default off) is on, the standard text otherwise — always editable. Email or
 *    WhatsApp.
 *  - "Send email" only when Outgoing email (`email_outbound`) is set up and on; otherwise the inline "not set up"
 *    note says so. Copying the text, "Open in mail app" / "Open WhatsApp" and "Log as sent" always work.
 *  - "Supplier replied with a date?" records the new promised date: it becomes the LPO's expected date (Forecast,
 *    the Dashboard and the LPO list move with it) and the old one is kept in the history shown below.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { CalendarClock, Copy, History, Mail, MessageCircle, Send, Sparkles } from 'lucide-react';
import api from '@/lib/api';
import { getErrorMessage } from '@/lib/errors';
import { AI_EXTRAS, FEATURE_STATUS, isFeatureNotEnabledError } from '@/lib/integrations';
import { formatDate, formatDateTime, formatNumber, toDateInputValue } from '@/lib/format';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { NotConfigured } from '@/components/ui/not-configured';
import { SidePanel, SidePanelSection } from '@/components/ui/side-panel';
import { Textarea } from '@/components/ui/textarea';
import { notify } from '@/components/ui/notify';
import { useFeature } from '@/hooks/useIntegrationStatus';
import { Segmented, plural } from './SfUi';
import { mailtoHref, whatsappHref } from './chaseModel';

async function copyText(value) {
  if (navigator?.clipboard?.writeText) {
    await navigator.clipboard.writeText(value);
    return;
  }
  const area = document.createElement('textarea');
  area.value = value;
  area.setAttribute('readonly', '');
  area.style.position = 'fixed';
  area.style.opacity = '0';
  document.body.appendChild(area);
  area.select();
  document.execCommand?.('copy');
  area.remove();
}

function HistoryList({ lpo }) {
  const chases = (lpo?.chases || []).map((item) => ({ kind: 'chase', at: item.at, item }));
  const etas = (lpo?.eta_history || []).map((item) => ({ kind: 'eta', at: item.at, item }));
  const rows = [...chases, ...etas].sort((a, b) => String(b.at).localeCompare(String(a.at)));
  if (!rows.length) return <p className="text-[12.5px] text-ink-3">Not chased yet.</p>;
  return (
    <ul className="space-y-2" data-testid="chase-history">
      {rows.map(({ kind, at, item }) => (
        <li key={`${kind}-${at}`} className="grid grid-cols-[16px_minmax(0,1fr)] gap-x-2 text-[12.5px]">
          {kind === 'chase' ? <Mail className="mt-0.5 h-3.5 w-3.5 text-ink-3" aria-hidden="true" /> : <CalendarClock className="mt-0.5 h-3.5 w-3.5 text-ink-3" aria-hidden="true" />}
          <span className="min-w-0">
            <span className="block text-ink">
              {kind === 'chase'
                ? `${item.sent ? 'Emailed' : 'Chased'} ${item.channel === 'whatsapp' ? 'on WhatsApp' : item.to?.length ? `· ${item.to.join(', ')}` : ''}`
                : `New ETA ${formatDate(item.to)}${item.from ? ` (was ${formatDate(item.from)})` : ''}`}
            </span>
            <span className="block text-[12px] text-ink-3">{formatDateTime(at, { relativeDay: true })} · {item.by || '—'}{item.note ? ` · ${item.note}` : ''}</span>
          </span>
        </li>
      ))}
    </ul>
  );
}

/**
 * props: open, onOpenChange, lpoId, number + supplier (shown while loading), onChanged (reload the page after a
 * chase or a new date).
 */
export function ChaseSupplierPanel({ open, onOpenChange, lpoId, number, supplier, onChanged }) {
  const email = useFeature('email_outbound');
  const aiExtras = useFeature(AI_EXTRAS);
  const [channel, setChannel] = useState('email');
  const [draft, setDraft] = useState(null);
  const [lpo, setLpo] = useState(null);
  const [form, setForm] = useState({ to: '', subject: '', body: '' });
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  const [eta, setEta] = useState({ date: '', note: '', error: '', saving: false });
  // Each channel's message is prepared once per opening and keeps the person's edits: switching Email ↔ WhatsApp
  // and back never asks the server (or the AI) again and never throws away what was typed.
  const draftsByChannel = useRef({});
  const requestId = useRef(0);

  const load = useCallback(async (nextChannel, { withLpo = false } = {}) => {
    if (!lpoId) return;
    const id = ++requestId.current;
    setLoading(true);
    setError('');
    try {
      const [draftRes, lpoRes] = await Promise.all([
        api.post(`/lpos/${lpoId}/chase-draft`, { channel: nextChannel }),
        withLpo ? api.get(`/lpos/${lpoId}`) : Promise.resolve(null),
      ]);
      if (id !== requestId.current) return; // switched again meanwhile
      setDraft(draftRes.data);
      if (lpoRes) setLpo(lpoRes.data);
      setForm((prev) => ({ to: prev.to || draftRes.data.to || '', subject: draftRes.data.subject || '', body: draftRes.data.body || '' }));
    } catch (err) {
      if (id === requestId.current) setError(getErrorMessage(err, 'Could not prepare the message'));
    } finally {
      if (id === requestId.current) setLoading(false);
    }
  }, [lpoId]);

  useEffect(() => {
    if (!open) return;
    draftsByChannel.current = {};
    setChannel('email');
    setForm({ to: '', subject: '', body: '' });
    setEta({ date: '', note: '', error: '', saving: false });
    setDraft(null);
    load('email', { withLpo: true });
  }, [open, load]);

  const switchChannel = (next) => {
    if (next === channel) return;
    if (draft) draftsByChannel.current[channel] = { draft, subject: form.subject, body: form.body };
    setChannel(next);
    const kept = draftsByChannel.current[next];
    if (kept) {
      requestId.current += 1; // a load still running for the other channel must not overwrite this one
      setLoading(false);
      setError('');
      setDraft(kept.draft);
      setForm((prev) => ({ ...prev, subject: kept.subject, body: kept.body }));
    } else {
      load(next);
    }
  };

  const copy = async () => {
    try {
      await copyText(channel === 'email' && form.subject ? `${form.subject}\n\n${form.body}` : form.body);
      notify.success('Message copied', { description: 'Paste it into your mail or WhatsApp, then press “Log as sent”.' });
    } catch {
      notify.error('Could not copy the text');
    }
  };

  const submit = async (send) => {
    if (!form.body.trim()) { setError('Write the message first'); return; }
    if (send && !form.to.trim()) { setError('Add the supplier’s email address'); return; }
    setBusy(send ? 'send' : 'log');
    setError('');
    try {
      const res = await api.post(`/lpos/${lpoId}/chase`, { channel, to: channel === 'email' ? form.to : '', subject: channel === 'email' ? form.subject : '', body: form.body, send });
      notify.success(send ? `Email sent to ${res.data.chase.to.join(', ')}` : `Chase of ${supplier || 'the supplier'} logged`, {
        description: send ? `${number} · a sent email cannot be undone` : `${number} · chase ${formatNumber(res.data.chase_count)}`,
      });
      onOpenChange(false);
      await onChanged?.();
    } catch (err) {
      if (isFeatureNotEnabledError(err)) email.refresh?.();
      setError(getErrorMessage(err, send ? 'Could not send the email' : 'Could not log the chase'));
    } finally {
      setBusy('');
    }
  };

  const saveEta = async (event) => {
    event.preventDefault();
    if (!eta.date) { setEta((prev) => ({ ...prev, error: 'Pick the date the supplier promised' })); return; }
    setEta((prev) => ({ ...prev, saving: true, error: '' }));
    try {
      const res = await api.post(`/lpos/${lpoId}/revised-eta`, { date: eta.date, note: eta.note });
      setLpo(res.data);
      setEta({ date: '', note: '', error: '', saving: false });
      notify.success(`${number} now expected ${formatDate(res.data.expected_start_delivery_date)}`, { description: res.data.original_expected_date ? `First promised ${formatDate(res.data.original_expected_date)}` : undefined });
      await onChanged?.();
    } catch (err) {
      setEta((prev) => ({ ...prev, saving: false, error: getErrorMessage(err, 'Could not save the new date') }));
    }
  };

  const facts = draft?.facts;
  const sendReady = email.enabled && channel === 'email';
  const fieldClass = 'h-9 text-[13px]';

  return (
    <SidePanel
      open={open}
      onOpenChange={onOpenChange}
      width="lg"
      icon={Sparkles}
      title={`Chase ${supplier || 'supplier'}`}
      subtitle={facts
        ? `${number} · ${facts.days_late ? `${facts.days_late} d late` : 'not late yet'} · ${formatNumber(facts.remaining_pcs)} pcs still to deliver`
        : number}
      testId="chase-panel"
      footer={(
        <footer className="flex flex-wrap items-center gap-2 border-t border-line-2 bg-surface px-4 py-3 sm:px-[22px]">
          {error ? <p className="w-full text-[12.5px] font-semibold text-status-red-ink" role="alert" data-testid="chase-error">{error}</p> : null}
          <Button variant="ghost" size="sm" onClick={copy} disabled={!form.body} data-testid="chase-copy"><Copy aria-hidden="true" />Copy text</Button>
          {channel === 'email' ? (
            <Button variant="ghost" size="sm" asChild>
              <a href={mailtoHref({ to: form.to, subject: form.subject, body: form.body })}><Mail aria-hidden="true" />Open in mail app</a>
            </Button>
          ) : (
            <Button variant="ghost" size="sm" asChild>
              <a href={whatsappHref(form.body)} target="_blank" rel="noreferrer"><MessageCircle aria-hidden="true" />Open WhatsApp</a>
            </Button>
          )}
          <span className="ml-auto flex items-center gap-2">
            <Button variant={sendReady ? 'outline' : 'default'} size="sm" onClick={() => submit(false)} disabled={Boolean(busy) || loading} data-testid="chase-log">
              {busy === 'log' ? 'Saving…' : 'Log as sent'}
            </Button>
            {sendReady ? (
              <Button size="sm" onClick={() => submit(true)} disabled={Boolean(busy) || loading} data-testid="chase-send">
                <Send aria-hidden="true" />{busy === 'send' ? 'Sending…' : 'Send email'}
              </Button>
            ) : null}
          </span>
        </footer>
      )}
    >
      <SidePanelSection title="Message">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <Segmented
            size="sm"
            ariaLabel="Send by"
            value={channel}
            onChange={switchChannel}
            testId="chase-channel"
            options={[{ key: 'email', label: 'Email', testId: 'chase-channel-email' }, { key: 'whatsapp', label: 'WhatsApp', testId: 'chase-channel-whatsapp' }]}
          />
          {draft ? (
            draft.source === 'ai' ? (
              <span className="inline-flex items-center gap-1 text-[12px] font-semibold text-status-violet-ink" data-testid="chase-source"><Sparkles className="h-3.5 w-3.5" aria-hidden="true" />Drafted by AI — check it before sending</span>
            ) : (
              <span className="text-[12px] text-ink-3" data-testid="chase-source">
                Standard text{draft.ai_error ? ' — the AI assistant was not available' : draft.ai_enabled ? '' : ' · AI wording is off'}
                {!draft.ai_error && !draft.ai_enabled && aiExtras.status === FEATURE_STATUS.OFF ? ' — an admin can switch on AI extras in Admin › Integrations' : ''}
              </span>
            )
          ) : null}
        </div>
        {loading && !draft ? (
          <div className="space-y-2" aria-busy="true">
            <span className="ui-skeleton-shimmer block h-9 rounded-control" />
            <span className="ui-skeleton-shimmer block h-48 rounded-control" />
          </div>
        ) : (
          <div className={cn('space-y-2.5', loading && 'opacity-60')}>
            {channel === 'email' ? (
              <>
                <div className="space-y-1">
                  <Label htmlFor="chase-to" className="text-[12px] font-semibold text-ink-2">To</Label>
                  <Input id="chase-to" className={fieldClass} value={form.to} onChange={(e) => setForm((prev) => ({ ...prev, to: e.target.value }))} placeholder="supplier@example.com" data-testid="chase-to" />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="chase-subject" className="text-[12px] font-semibold text-ink-2">Subject</Label>
                  <Input id="chase-subject" className={fieldClass} value={form.subject} onChange={(e) => setForm((prev) => ({ ...prev, subject: e.target.value }))} data-testid="chase-subject" />
                </div>
              </>
            ) : null}
            <div className="space-y-1">
              <Label htmlFor="chase-body" className="text-[12px] font-semibold text-ink-2">Message</Label>
              <Textarea id="chase-body" rows={channel === 'email' ? 11 : 7} value={form.body} onChange={(e) => setForm((prev) => ({ ...prev, body: e.target.value }))} className="text-[13px] leading-relaxed" data-testid="chase-body" />
            </div>
            {channel === 'email' && !email.loading && !email.enabled ? (
              <NotConfigured feature="email_outbound" layout="inline" text="Sending from Sara needs Outgoing email — copy the text or open your mail app, then log it." testId="chase-email-not-configured" />
            ) : null}
          </div>
        )}
      </SidePanelSection>
      <SidePanelSection title="Supplier replied with a date?">
        <form onSubmit={saveEta} className="grid grid-cols-1 gap-2 sm:grid-cols-[150px_minmax(0,1fr)_auto] sm:items-end" data-testid="chase-eta-form">
          <div className="space-y-1">
            <Label htmlFor="chase-eta" className="text-[12px] font-semibold text-ink-2">New delivery date</Label>
            <Input id="chase-eta" type="date" min={toDateInputValue(new Date())} className={fieldClass} value={eta.date} onChange={(e) => setEta((prev) => ({ ...prev, date: e.target.value }))} data-testid="chase-eta-date" />
          </div>
          <div className="space-y-1">
            <Label htmlFor="chase-eta-note" className="text-[12px] font-semibold text-ink-2">Note (optional)</Label>
            <Input id="chase-eta-note" className={fieldClass} value={eta.note} onChange={(e) => setEta((prev) => ({ ...prev, note: e.target.value }))} placeholder="e.g. part shipment of 200 first" />
          </div>
          <Button type="submit" variant="outline" size="sm" className="h-9" disabled={eta.saving} data-testid="chase-eta-save">
            <CalendarClock aria-hidden="true" />{eta.saving ? 'Saving…' : 'Save new ETA'}
          </Button>
          {eta.error ? <p className="text-[12.5px] font-semibold text-status-red-ink sm:col-span-3" role="alert">{eta.error}</p> : null}
        </form>
        <p className="text-[12px] text-ink-3">
          {lpo?.expected_start_delivery_date ? <>Expected now: <b className="font-semibold text-ink-2">{formatDate(lpo.expected_start_delivery_date)}</b></> : 'No expected date yet'}
          {lpo?.original_expected_date ? <> · first promised {formatDate(lpo.original_expected_date)}</> : null}
          {' · '}the old date stays in the history.
        </p>
      </SidePanelSection>
      {facts?.lines?.length || draft?.lines?.length ? (
        <SidePanelSection title={`Still to deliver · ${plural((draft.lines || []).length, 'line')}`} collapsible defaultOpen={false}>
          <ul className="divide-y divide-line-2 text-[12.5px]">
            {(draft.lines || []).map((line) => (
              <li key={line.part_number} className="flex min-w-0 items-center gap-2 py-1.5">
                <span className="font-mono text-ink">{line.part_number}</span>
                <span className="min-w-0 truncate text-ink-3">{line.description}</span>
                <span className="ml-auto shrink-0 font-semibold tabular-nums text-status-amber-ink">{formatNumber(line.remaining)} pcs</span>
              </li>
            ))}
          </ul>
        </SidePanelSection>
      ) : null}
      <SidePanelSection title="History" actions={<History className="h-3.5 w-3.5 text-ink-3" aria-hidden="true" />}>
        <HistoryList lpo={lpo} />
      </SidePanelSection>
    </SidePanel>
  );
}
